import random from 'lodash/random';

import anime from 'animejs/lib/anime.es.js';
import { calculatePadding, calculateTranslateXPosition, calculateTranslateYPosition } from './calculateTranslate';
import options from './options';

/**
 * Animates the message to fullscreen
 * @returns {Promise<* | never>} fulfills after moved to fullscreen, async animations effects might still be happening
 */
export const animateFullScreen = function animateFullScreen() {
	const { duration, fullscreenScale, userNameDefaultVisibility } = this.props;

	if (this.messageContainer.current) {
		this.messageContainer.current.style.visibility = 'visible';
	}

	if (this.contentContainer) {
		anime({
			targets: this.contentContainer.current,
			padding: this.scaledPadding,
			duration,
			elasticity: duration,
			easing: 'easeInOutSine',
		});
	}

	if (this.messageContainerAnimation) {
		this.messageContainerAnimation.pause();
	}

	this.messageContainerAnimation = anime({
		targets: this.messageContainer.current,
		scale: fullscreenScale,
		duration,
		translateX: this.translateXpos,
		translateY: this.translateYpos,
		elasticity: duration,
		easing: 'easeInOutSine',
	});

	this.messageContainerAnimation.finished.then(() => {
		if (this.name.current) {
			this.name.current.style.visibility = 'visible';
		}

		if (this.rotateAnime) {
			this.rotateAnime.pause();
		}

		if (this.decoration) {
			anime({
				targets: this.decoration.current,
				duration,
				elasticity: duration,
				opacity: 0,
				easing: 'easeInOutSine',
			});
		}

		if (userNameDefaultVisibility === 'invisible') {
			const nameAnimation = anime({
				targets: this.name.current,
				duration,
				elasticity: duration,
				opacity: 1,
				easing: 'easeInOutSine',
			});
			nameAnimation.finished.then(() => {
				if (this.rotateUserAnime) {
					this.rotateUserAnime.play();
				}
			});
		}
	});

	return this.messageContainerAnimation.finished;
};

/**
 * Animates the message to the background, message disappears into background
 * @returns {Promise<* | never>} fulfills after moved to background position, async animations effects might still be happening
 */
export const animateToBackground = function animateToBackground() {
	const { duration, userNameDefaultVisibility } = this.props;

	if (this.rotateAnime) {
		this.rotateAnime.pause();
	}

	if (this.messageContainerAnimation) {
		this.messageContainerAnimation.pause();
	}

	this.messageContainerAnimation = anime({
		targets: this.messageContainer.current,
		scale: 0,
		duration,
		translateX: -1000,
		translateY: -1000,
		elasticity: duration,
		easing: 'easeInOutSine',
	});

	this.messageContainerAnimation.finished.then(() => {
		if (userNameDefaultVisibility === 'invisible') {
			const nameAnimation = anime({
				targets: this.name.current,
				duration,
				elasticity: duration,
				opacity: 0,
				easing: 'easeInOutSine',
			});

			nameAnimation.finished.then(() => {
				if (this.name.current) {
					this.name.current.style.visibility = 'invisible';
				}
			});
		}

		if (this.rotateUserAnime) {
			this.rotateUserAnime.pause();
		}

		if (this.messageContainer.current) {
			this.messageContainer.current.style.visibility = 'hidden';
		}
	});

	return this.messageContainerAnimation.finished;
};

/**
 * Bind a anime to the wrapperContainer
 * We bind this not on the messageContainer on purpose because 2 different anime's on the same element doesn't work
 */
export const bindRotateAnimation = function bindAnimation() {
	const rotationAngleNeg = random(options.minWiggleAngle, options.maxWiggleAngle);
	const rotationAnglePos = random(-options.maxWiggleAngle, -options.minWiggleAngle);
	const direction = random(0, 1);
	const rotationAngle = direction ? rotationAnglePos : rotationAngleNeg;

	return anime({
		direction: 'alternate',
		loop: true,
		autoplay: false,
		delay: random(0, 400),
		easing: 'easeInOutSine',
		duration: random(options.minWiggleDuration, options.maxWiggleDuration),
		targets: this.wrapperContainer.current,
		rotate: [0.1, rotationAngle], // the start 0.1 is a hack here this is to prevent some jank
	});
};

/**
 * Bind a anime to the userContainer
 * We bind this not on the user on purpose because 2 different anime's on the same element doesn't work
 */
export const bindUserRotateAnimation = function bindUserRotateAnimation() {
	const rotationAngleNeg = random(options.minUserWiggleAngle, options.maxUserWiggleAngle);
	const rotationAnglePos = random(-options.maxUserWiggleAngle, -options.minUserWiggleAngle);
	const direction = random(0, 1);
	const rotationAngle = direction ? rotationAnglePos : rotationAngleNeg;

	return anime({
		direction: 'alternate',
		loop: true,
		autoplay: false,
		delay: random(0, 400),
		easing: 'easeInOutSine',
		duration: random(options.minWiggleDuration, options.maxWiggleDuration),
		targets: this.userContainer.current,
		rotate: [0.1, rotationAngle], // the start 0.1 is a hack here this is to prevent some jank
	});
};

/**
 * Animates the message to its original position in the grid
 * @returns {Promise<* | never>} fulfills after moved to gird position, async animations effects might still be happening
 */
export const animateToGrid = function animateToGrid() {
	const { duration, userNameDefaultVisibility } = this.props;

	if (this.messageContainer.current) {
		this.messageContainer.current.style.visibility = 'visible';
	}

	if (this.rotateAnime) {
		this.rotateAnime.play();
	}

	if (this.contentContainer) {
		anime({
			targets: this.contentContainer.current,
			padding: 10,
			duration,
			elasticity: duration,
			easing: 'easeInOutSine',
		});
	}

	if (this.messageContainerAnimation) {
		this.messageContainerAnimation.pause();
	}

	this.messageContainerAnimation = anime({
		targets: this.messageContainer.current,
		scale: options.gridScale,
		duration,
		translateX: 0,
		translateY: 0,
		elasticity: duration,
		easing: 'easeInOutSine',
	});

	this.messageContainerAnimation.finished.then(() => {
		if (this.decoration) {
			anime({
				targets: this.decoration.current,
				duration,
				elasticity: duration,
				opacity: 1,
				easing: 'easeInOutSine',
			});
		}

		if (userNameDefaultVisibility === 'invisible') {
			const userAnimation = anime({
				targets: this.name.current,
				duration,
				elasticity: duration,
				opacity: 0,
				easing: 'easeInOutSine',
			});
			userAnimation.finished.then(() => {
				if (this.name.current) {
					this.name.current.style.visibility = userNameDefaultVisibility;
				}
			});
		}

		if (this.rotateUserAnime) {
			this.rotateUserAnime.pause();
		}
	});

	return this.messageContainerAnimation.finished;
};

/**
 * Set the initial animation state of the elements
 * @returns {Promise<* | never>} fulfills after the move is complete
 */
export const prepareAnimations = function prepareAnimations() {
	const {
		show, fullscreen, focussed, fullscreenScale, id,
	} = this.props;
	this.translateXpos = calculateTranslateXPosition(window.document.body.offsetWidth, this.messageContainer.current, fullscreenScale);
	this.translateYpos = calculateTranslateYPosition(window.innerHeight, this.messageContainer.current, fullscreenScale);
	if (this.contentContainer) {
		this.scaledPadding = calculatePadding(this.contentContainer.current, fullscreenScale);
	}
	this.rotateAnime = this.rotateAnime ? this.rotateAnime : this.bindRotateAnimation();
	if (this.userContainer) {
		this.rotateUserAnime = this.rotateUserAnime ? this.rotateUserAnime : this.bindUserRotateAnimation();
	}

	if (show && !focussed) {
		return this.animateToBackground()
			.then(this.animateToGrid)
			.then(() => {
				if (fullscreen) {
					this.animateFullScreen();
				}
			});
	}

	if (focussed === id) {
		return this.animateFullScreen();
	}

	return new Promise(resolve => resolve());
};

/**
 * Based on the properties triggers the required animation on the references
 * @param prevProps
 * @returns {Promise<*|never>} fulfills when the animation move is complete
 */
export const toggleAnimations = function toggleAnimations(prevProps) {
	const {
		willBeDeleted, fullscreen, show, fullscreenScale,
	} = this.props;
	const showChanged = show !== prevProps.show;
	const willBeDeletedChanged = willBeDeleted !== prevProps.willBeDeleted;
	const fullscreenChanged = fullscreen !== prevProps.fullscreen;
	if (showChanged && fullscreenChanged && !prevProps.show && !prevProps.fullscreen && fullscreen && show) {
		// console.log(`### ${new Date().toISOString()}} ###>`, id, 'Item was not shown and not fullscreen but should be shown and fullscreen');
		this.messageContainer.current.style.visibility = 'visible';
		this.translateXpos = this.translateXpos ? this.translateXpos : calculateTranslateXPosition(window.document.body.offsetWidth, this.messageContainer.current, fullscreenScale);
		this.translateYpos = this.translateYpos ? this.translateYpos : calculateTranslateYPosition(window.innerHeight, this.messageContainer.current, fullscreenScale);
		this.scaledPadding = this.scaledPadding || !this.contentContainer ? this.scaledPadding : calculatePadding(this.contentContainer.current, fullscreenScale);
		return this.animateFullScreen();
	}
	if (showChanged && fullscreenChanged && prevProps.show && prevProps.fullscreen && !fullscreen && !show) {
		return this.animateToBackground();
	}
	if (showChanged && !prevProps.show && show) {
		return new Promise((resolve) => {
			setTimeout(resolve, options.animationDuration * 1.5);
		})
			.then(this.animateToGrid);
	}
	if (showChanged && prevProps.show && !show) {
		return this.animateToBackground();
	}
	if (willBeDeletedChanged && !prevProps.willBeDeleted && willBeDeleted) {
		// console.log(`### ${new Date().toISOString()} ###>`, id, 'Will be deleted');
		return this.animateToBackground();
	}
	if (fullscreenChanged && !prevProps.fullscreen && fullscreen) {
		// console.log(`### ${new Date().toISOString()} ###>`, id, 'Grid and should be fullscreen');
		this.translateXpos = this.translateXpos ? this.translateXpos : calculateTranslateXPosition(window.document.body.offsetWidth, this.messageContainer.current, fullscreenScale);
		this.translateYpos = this.translateYpos ? this.translateYpos : calculateTranslateYPosition(window.innerHeight, this.messageContainer.current, fullscreenScale);
		this.scaledPadding = this.scaledPadding || !this.contentContainer ? this.scaledPadding : calculatePadding(this.contentContainer.current, fullscreenScale);
		// eslint-disable-next-line no-undef
		return this.animateFullScreen();
	}
	if (fullscreenChanged && prevProps.fullscreen && !fullscreen) {
		return this.animateToGrid();
	}

	return new Promise((resolve) => {
		resolve();
	});
};



// WEBPACK FOOTER //
// ./src/ui/interaction/children/animatedVisualizations/animations.js